import {
  animate,
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { Ban, Lock, Unlock } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { localCardImage } from "@/lib/card-images";
import { manaMeta } from "@/lib/mana";
import type {
  CardArt,
  CardScoreBreakdown,
  DeckCategoryId,
  DeckToken,
  RecommendationReason,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { CardZoom } from "./CardZoom";
import { ManaCost } from "./ManaSymbol";

export interface CardDisplayProps {
  name: string;
  art: CardArt | null;
  quantity?: number;
  score?: CardScoreBreakdown;
  reasons?: readonly RecommendationReason[];
  primaryCategory?: DeckCategoryId;
  locked?: boolean;
  excluded?: boolean;
  /** Offer the oracle text and score panel in the enlarged view. */
  flippable?: boolean;
  /** Max tilt in degrees. */
  tilt?: number;
  /**
   * Now and then drift slowly through the hover tilt and sheen on its own,
   * while on screen and not hovered. Off by default (card grids).
   */
  idle?: boolean;
  className?: string;
  priority?: boolean;
  /** Optional activation action. Defaults to opening the card zoom. */
  onClick?: () => void;
  nameOf?: (oracleId: string) => string;
  relatedTokens?: readonly DeckToken[];
  onLock?: (element: HTMLElement) => void;
  onExclude?: (element: HTMLElement) => void;
}

export function glowColor(art: CardArt | null): string {
  const colors = art?.colors ?? [];
  if (colors.length === 0) {
    return art?.typeLine.includes("Land") ? "#a07118" : "#c9c2bd";
  }
  if (colors.length > 1) {
    return "#e3ad33";
  }
  return manaMeta[colors[0] ?? "C"].hex;
}

/**
 * A Magic card with a 3D tilt, holographic sheen and glow on hover. A click
 * enlarges the card in place (see CardZoom).
 */
export const CardDisplay = React.memo(function CardDisplay({
  name,
  art,
  quantity = 1,
  score,
  reasons = [],
  primaryCategory,
  locked = false,
  excluded = false,
  flippable = true,
  tilt = 14,
  idle = false,
  className,
  priority = false,
  onClick,
  nameOf = (id) => id,
  relatedTokens,
  onLock,
  onExclude,
}: CardDisplayProps) {
  const reduceMotion = useReducedMotion();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  // `zoomOrigin` stays set until the close animation has finished.
  const [zoomOrigin, setZoomOrigin] = React.useState<DOMRect | null>(null);
  const [zoomOpen, setZoomOpen] = React.useState(false);

  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const spring = { stiffness: 220, damping: 18, mass: 0.6 };
  const rotateX = useSpring(useTransform(py, [0, 1], [tilt, -tilt]), spring);
  const rotateY = useSpring(useTransform(px, [0, 1], [-tilt, tilt]), spring);
  const sheenX = useTransform(px, (v) => `${v * 100}%`);
  const sheenY = useTransform(py, (v) => `${v * 100}%`);
  const sheen = useMotionTemplate`radial-gradient(circle at ${sheenX} ${sheenY}, rgba(255,255,255,0.32), transparent 45%)`;
  const idleSheen = useMotionValue(0);

  const glow = glowColor(art);
  // Local file only (public/cards/images); never a Scryfall URL.
  const image = localCardImage(art?.imageNormal ?? art?.imageLarge);

  const tracksPointer = !reduceMotion && tilt > 0;
  const handlePointerMove = tracksPointer
    ? (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === "touch") {
          return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        px.set((event.clientX - rect.left) / rect.width);
        py.set((event.clientY - rect.top) / rect.height);
      }
    : undefined;

  const reset = () => {
    px.set(0.5);
    py.set(0.5);
    setHovered(false);
  };

  const openZoom = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper || zoomOrigin) return;
    reset();
    setZoomOrigin(wrapper.getBoundingClientRect());
    setZoomOpen(true);
  };
  const activate = onClick ?? openZoom;

  useIdleDrift(
    idle && tracksPointer && !hovered && zoomOrigin === null,
    wrapperRef,
    px,
    py,
    idleSheen,
  );

  const closeZoom = React.useCallback(() => {
    setZoomOpen(false);
    cardRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cn("perspective group/card relative aspect-[63/88] w-full", className)}
      onPointerMove={handlePointerMove}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={reset}
    >
      <motion.div
        ref={cardRef}
        role="button"
        tabIndex={0}
        aria-haspopup={onClick ? undefined : "dialog"}
        aria-expanded={onClick ? undefined : zoomOpen}
        aria-label={onClick ? `Choose ${name} as commander` : `${name}. Press to enlarge.`}
        onClick={activate}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activate();
          }
        }}
        className={cn(
          "preserve-3d relative h-full w-full rounded-[4.75%/3.4%] outline-none focus-visible:ring-2 focus-visible:ring-gold-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          onClick ? "cursor-pointer" : "cursor-zoom-in",
          excluded && "opacity-40 grayscale",
          zoomOrigin && "opacity-0",
        )}
        style={{ rotateX, rotateY }}
        animate={{
          scale: hovered && !reduceMotion ? 1.06 : 1,
          z: hovered ? 40 : 0,
          rotateZ: hovered && !reduceMotion ? -1.2 : 0,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <div className="preserve-3d relative h-full w-full">
          <div
            className="backface-hidden absolute inset-0 overflow-hidden rounded-[4.75%/3.4%] bg-black transition-shadow duration-300"
            style={{
              boxShadow: hovered
                ? `0 0 34px 2px ${glow}aa, 0 30px 50px -20px #000`
                : "0 14px 30px -14px #000",
            }}
          >
            {image && !failed ? (
              <>
                {!loaded && <div className="skeleton-shimmer absolute inset-0" />}
                <img
                  src={image}
                  alt={name}
                  loading={priority ? "eager" : "lazy"}
                  decoding="async"
                  draggable={false}
                  onLoad={() => setLoaded(true)}
                  onError={() => setFailed(true)}
                  className={cn(
                    "h-full w-full object-cover transition-opacity duration-500",
                    loaded ? "opacity-100" : "opacity-0",
                  )}
                />
              </>
            ) : (
              <TextFrame name={name} art={art} />
            )}
            <motion.div
              className="pointer-events-none absolute inset-0 mix-blend-overlay"
              style={{ background: sheen, opacity: hovered ? 1 : idleSheen }}
            />
            <div
              className={cn(
                "foil pointer-events-none absolute inset-0 transition-opacity duration-300",
                hovered ? "opacity-40" : "opacity-0",
              )}
            />
            {quantity > 1 && (
              <span className="absolute left-2 top-[11%] rounded-full border border-gold-300/60 bg-obsidian-950/90 px-2 py-0.5 font-display text-sm font-bold text-gold-200 shadow-glow">
                ×{quantity}
              </span>
            )}
            {locked && (
              <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-gold-400/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-obsidian-950">
                <Lock className="h-3 w-3" /> Locked
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {(onLock || onExclude) && (
        <div className="absolute right-2 top-[11%] z-10 flex flex-col gap-1.5 opacity-100 transition-opacity duration-200 sm:opacity-0 sm:group-hover/card:opacity-100 sm:group-focus-within/card:opacity-100">
          {onLock && (
            <CardAction
              label={locked ? `Unlock ${name}` : `Lock ${name} into the deck`}
              active={locked}
              onClick={() => cardRef.current && onLock(cardRef.current)}
            >
              {locked ? <Unlock /> : <Lock />}
            </CardAction>
          )}
          {onExclude && (
            <CardAction
              label={excluded ? `Allow ${name} again` : `Exclude ${name} from the next build`}
              active={excluded}
              danger
              onClick={() => cardRef.current && onExclude(cardRef.current)}
            >
              <Ban />
            </CardAction>
          )}
        </div>
      )}

      {zoomOrigin &&
        createPortal(
          <AnimatePresence onExitComplete={() => setZoomOrigin(null)}>
            {zoomOpen && (
              <CardZoom
                origin={zoomOrigin}
                name={name}
                art={art}
                glow={glow}
                score={score}
                reasons={reasons}
                primaryCategory={primaryCategory}
                showDetails={flippable}
                nameOf={nameOf}
                relatedTokens={relatedTokens}
                fallback={<TextFrame name={name} art={art} />}
                onClose={closeZoom}
              />
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
});

const IDLE_PAUSE_MS: readonly [number, number] = [2500, 7000];
const IDLE_DRIFT_S = 3.2;
const IDLE_HOLD_MS = 900;

/**
 * Idle "hover": after a random pause, tween the pointer motion values slowly
 * to a random point and back, fading the sheen in and out. Only motion values
 * change (no React state per frame); paused offscreen and in hidden tabs.
 */
function useIdleDrift(
  enabled: boolean,
  ref: React.RefObject<HTMLElement | null>,
  px: MotionValue<number>,
  py: MotionValue<number>,
  sheen: MotionValue<number>,
) {
  const [inView, setInView] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(
    () => typeof document === "undefined" || document.visibilityState === "visible",
  );

  React.useEffect(() => {
    const element = ref.current;
    if (!enabled || !element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) =>
      setInView(entry?.isIntersecting ?? false),
    );
    observer.observe(element);
    const onVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, ref]);

  const active = enabled && inView && pageVisible;
  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controls: { stop: () => void }[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      });
    const tween = (x: number, y: number, s: number) => {
      const options = {
        duration: IDLE_DRIFT_S,
        ease: [0.45, 0, 0.55, 1] as [number, number, number, number],
      };
      controls = [animate(px, x, options), animate(py, y, options), animate(sheen, s, options)];
      return Promise.all(controls);
    };
    const random = (min: number, max: number) => min + Math.random() * (max - min);

    void (async () => {
      // Desynchronise cards that start together.
      await wait(random(600, IDLE_PAUSE_MS[0]));
      while (!cancelled) {
        const angle = random(0, Math.PI * 2);
        const reach = random(0.18, 0.32);
        await tween(0.5 + Math.cos(angle) * reach, 0.5 + Math.sin(angle) * reach, 0.75);
        if (cancelled) return;
        await wait(IDLE_HOLD_MS);
        if (cancelled) return;
        await tween(0.5, 0.5, 0);
        if (cancelled) return;
        await wait(random(...IDLE_PAUSE_MS));
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      for (const control of controls) control.stop();
      px.set(0.5);
      py.set(0.5);
      sheen.set(0);
    };
  }, [active, px, py, sheen]);
}

function CardAction({
  children,
  label,
  active,
  danger = false,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  active: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      whileHover={{ scale: 1.15 }}
      whileTap={{ scale: 0.85 }}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-md transition-colors [&_svg]:h-4 [&_svg]:w-4",
        active
          ? danger
            ? "border-red-300 bg-red-500/90 text-white"
            : "border-gold-200 bg-gold-400 text-obsidian-950 shadow-glow"
          : "border-white/20 bg-obsidian-950/80 text-gold-100 hover:border-gold-300",
      )}
    >
      {children}
    </motion.button>
  );
}

/** Placeholder when no local image exists: a stylized frame with the card's text. */
export function TextFrame({ name, art }: { name: string; art: CardArt | null }) {
  return (
    <div className="flex h-full w-full flex-col gap-2 border-[6px] border-black bg-gradient-to-b from-obsidian-800 to-obsidian-950 p-3">
      <div className="flex items-center justify-between rounded-md bg-black/40 px-2 py-1">
        <span className="font-display text-xs font-bold text-gold-100">{name}</span>
        {art?.manaCost && <ManaCost cost={art.manaCost} symbolClassName="h-3 w-3" />}
      </div>
      <div className="flex flex-1 items-center justify-center rounded-md bg-black/30 text-4xl text-gold-400/40">
        ✦
      </div>
      <p className="rounded-md bg-black/40 px-2 py-1 text-[10px] italic text-gold-200/80">
        {art?.typeLine ?? "Card"}
      </p>
    </div>
  );
}

export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "skeleton-shimmer aspect-[63/88] w-full rounded-[4.75%/3.4%] border border-white/5",
        className,
      )}
    />
  );
}
